/**
 * QueryBridge — wires SessionRunner lifecycle state to QueryExecutor execution.
 *
 * SessionRunner handles connect/idle/message lifecycle.
 * QueryExecutor handles Claude SDK query orchestration.
 * QueryBridge adapts between them by constructing the QueryHost interface
 * that QueryExecutor expects, using live references to ModeController state.
 */

import type { HarnessEvent, HarnessUserMessage, PtyBridge } from "../harness/index.js";
import { createHarness, type HarnessKind } from "../harness/index.js";
import {
  runSdkQuery,
  runPassiveTurn,
  resolvePromptDelivery,
  hasExistingSessionFile,
  type QueryHost,
} from "../execution/query-executor.js";
import type { AgentConnection } from "../connection/agent-connection.js";
import type { ModeController } from "./mode-controller.js";
import type { AgentRunnerConfig, AgentRunnerCallbacks } from "../runner-types.js";
import type { TaskContext, AgentMode, MultimodalBlock } from "@project/shared";
import { createServiceLogger } from "../utils/logger.js";

const logger = createServiceLogger("QueryBridge");

/**
 * Pick the harness implementation for the task chat path.
 *
 * Card/task chat is ALWAYS the PTY harness (drives the `claude` CLI under a
 * pseudo-terminal, rendered as the Connected TUI). This is unconditional: the
 * env var `CONVEYOR_HARNESS` is deliberately NOT consulted, so a stale value
 * (a leftover codespace secret, or one applied via the bootstrap envVars) can
 * never route a card to the headless SDK loop — the failure mode that left
 * auto-mode cards running SDK with no Connected-TUI tab.
 *
 * The ONLY way to put a card on the SDK harness is an explicit maintainer
 * override, `CONVEYOR_FORCE_SDK_CARDS=1`. It is a distinct name that is never
 * pushed as a codespace secret nor emitted in the bootstrap env, so it cannot
 * be triggered accidentally — it exists solely for emergency local debugging.
 *
 * Scope: this governs ONLY the task chat. The audit and project-chat paths
 * construct the SDK harness directly via `createHarness()` (no-arg) and never
 * call this function, so they stay on SDK regardless.
 */
export function resolveHarnessKind(): HarnessKind {
  return process.env.CONVEYOR_FORCE_SDK_CARDS === "1" ? "sdk" : "pty";
}

/**
 * Adapt an AgentConnection into the harness-neutral PtyBridge consumed by the
 * PTY harness. Keeps the harness package free of any connection-layer import.
 */
function buildPtyBridge(connection: AgentConnection): PtyBridge {
  return {
    sendOutput: (data, dims) => connection.sendPtyOutput(data, dims),
    sendChatEvent: (event) => connection.sendPtyChatEvent(event),
    sendEnded: () => connection.sendPtyEnded(),
    onInput: (handler) => connection.onPtyInput(handler),
    onResize: (handler) => connection.onPtyResize(handler),
  };
}

export class QueryBridge {
  private readonly harness;
  /** Which harness drives this bridge ("pty" task chat; "sdk" only under the
   *  CONVEYOR_FORCE_SDK_CARDS maintainer override). */
  readonly harnessKind: HarnessKind;
  private readonly sessionIds = new Map<string, string>();
  private activeQuery: AsyncGenerator<HarnessEvent, void> | null = null;
  private readonly pendingToolOutputs: { tool: string; output: string }[] = [];
  private _stopped = false;
  private _discoveryCompleted = false;
  private _isParentTask = false;
  private _wasRateLimited = false;
  private _keyCycleCount = 0;
  private _abortController: AbortController | null = null;

  /** Called by SessionRunner when ExitPlanMode triggers a mode transition. */
  onModeTransition?: (newMode: AgentMode) => void;

  /** Called by tool handlers to soft-stop (abort query, keep session alive). */
  onSoftStop?: () => void;

  constructor(
    private readonly connection: AgentConnection,
    private readonly mode: ModeController,
    private readonly runnerConfig: AgentRunnerConfig,
    private readonly callbacks: AgentRunnerCallbacks,
  ) {
    const harnessKind = resolveHarnessKind();
    this.harnessKind = harnessKind;
    this.harness = createHarness(
      harnessKind,
      harnessKind === "pty" ? buildPtyBridge(connection) : undefined,
    );
  }

  get isStopped(): boolean {
    return this._stopped;
  }

  get isDiscoveryCompleted(): boolean {
    return this._discoveryCompleted;
  }

  set isDiscoveryCompleted(val: boolean) {
    this._discoveryCompleted = val;
  }

  get isParentTask(): boolean {
    return this._isParentTask;
  }

  set isParentTask(val: boolean) {
    this._isParentTask = val;
  }

  get wasRateLimited(): boolean {
    return this._wasRateLimited;
  }

  /**
   * Ask the live terminal (PTY harness only) to redraw its full screen.
   * Safe no-op on the SDK harness or between queries.
   */
  forceRepaint(): void {
    this.harness.forceRepaint?.();
  }

  stop(): void {
    this._stopped = true;
    this._abortController?.abort();
  }

  resume(): void {
    this._stopped = false;
  }

  /**
   * Tear down any parked/active CLI process the harness is keeping alive between
   * turns (PTY keep-alive). Called by SessionRunner on stop/shutdown so the
   * process never outlives the session and the Connected-TUI tab hides. No-op on
   * the SDK harness.
   */
  async dispose(): Promise<void> {
    await this.harness.dispose?.();
  }

  /**
   * Subscribe to "a human typed into the parked, idle TUI" (PTY keep-alive).
   * SessionRunner uses this to wake a passive turn. Returns unsubscribe; no-op
   * (returns a noop unsubscribe) on the SDK harness.
   */
  onPassiveActivity(handler: () => void): () => void {
    return this.harness.onPassiveActivity?.(handler) ?? (() => {});
  }

  /**
   * How the INITIAL instructions for this task would be delivered — "prefill"
   * means the TUI input is pre-filled but unsubmitted, awaiting the human.
   * Mirrors the decision runSdkQuery makes for the initial (non-follow-up)
   * query so SessionRunner can pick the matching state/timers.
   */
  initialPromptDelivery(context: TaskContext): "submit" | "prefill" {
    return resolvePromptDelivery({
      harnessKind: this.harnessKind,
      runnerMode: this.runnerConfig.mode,
      isAuto: this.mode.isAuto,
      agentMode: this.mode.effectiveMode,
      isFollowUp: false,
      hasExistingSession: hasExistingSessionFile(context.taskId, this.runnerConfig.workspaceDir, {
        agentMode: this.mode.effectiveMode,
        runnerMode: this.runnerConfig.mode,
      }),
    });
  }

  /**
   * Execute a Claude SDK query.
   * Without followUpContent: runs initial mode execution (build/plan).
   * With followUpContent: processes a follow-up user message.
   * `promptDelivery` overrides the executor's submit-vs-prefill resolution —
   * SessionRunner forces "submit" when pending chat messages already direct
   * the agent (prefilling would park them until a human touched the TUI).
   */
  async execute(
    context: TaskContext,
    followUpContent?: string | MultimodalBlock[],
    promptDelivery?: "submit" | "prefill",
  ): Promise<void> {
    this._stopped = false;
    this._wasRateLimited = false;
    this._abortController = new AbortController();
    const host = this.buildHost();
    try {
      await runSdkQuery(host, context, followUpContent, promptDelivery);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      const isAbort = this._stopped || /abort/i.test(msg);
      if (isAbort) {
        logger.info("Query stopped by user", { error: msg });
      } else {
        logger.error("Query execution failed", { error: msg });
        this.connection.sendEvent({ type: "error", message: msg });
      }
    } finally {
      this.mode.pendingModeRestart = false;
      this._abortController = null;
    }
  }

  /**
   * Drive a passive turn: the parked CLI is producing transcript records because
   * a human typed into the idle Connected-TUI. Mirrors execute()'s setup (fresh
   * abort controller so a superseding chat message can abort, host construction,
   * error classification) but runs the promptless passive drain instead of a
   * full query.
   */
  async executePassive(context: TaskContext): Promise<void> {
    this._stopped = false;
    this._wasRateLimited = false;
    this._abortController = new AbortController();
    const host = this.buildHost();
    try {
      await runPassiveTurn(host, context);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      const isAbort = this._stopped || /abort/i.test(msg);
      if (isAbort) {
        logger.info("Passive turn stopped", { error: msg });
      } else {
        logger.error("Passive turn failed", { error: msg });
        this.connection.sendEvent({ type: "error", message: msg });
      }
    } finally {
      this.mode.pendingModeRestart = false;
      this._abortController = null;
    }
  }

  // ── QueryHost construction ──────────────────────────────────────────

  private buildHost(): QueryHost {
    // oxlint-disable-next-line no-this-alias -- closure needed for live getter/setter delegation
    const bridge = this;
    return {
      config: this.runnerConfig,
      connection: this.connection,
      callbacks: this.callbacks,
      harness: this.harness,
      harnessKind: this.harnessKind,
      setupLog: [],
      sessionIds: this.sessionIds,
      pendingToolOutputs: this.pendingToolOutputs,

      // Live getters/setters delegating to ModeController + bridge state
      get agentMode() {
        return bridge.mode.effectiveMode;
      },
      get isAuto() {
        return bridge.mode.isAuto;
      },
      get isParentTask() {
        return bridge._isParentTask;
      },
      get hasExitedPlanMode() {
        return bridge.mode.hasExitedPlanMode;
      },
      set hasExitedPlanMode(val: boolean) {
        bridge.mode.hasExitedPlanMode = val;
      },
      get pendingModeRestart() {
        return bridge.mode.pendingModeRestart;
      },
      set pendingModeRestart(val: boolean) {
        bridge.mode.pendingModeRestart = val;
      },
      get discoveryCompleted() {
        return bridge._discoveryCompleted;
      },
      set discoveryCompleted(val: boolean) {
        bridge._discoveryCompleted = val;
      },
      get wasRateLimited() {
        return bridge._wasRateLimited;
      },
      set wasRateLimited(val: boolean) {
        bridge._wasRateLimited = val;
      },
      get keyCycleCount() {
        return bridge._keyCycleCount;
      },
      set keyCycleCount(val: number) {
        bridge._keyCycleCount = val;
      },
      get activeQuery() {
        return bridge.activeQuery;
      },
      set activeQuery(val: AsyncGenerator<HarnessEvent, void> | null) {
        bridge.activeQuery = val;
      },
      get abortController() {
        return bridge._abortController;
      },

      isStopped: () => bridge._stopped,
      requestStop: () => bridge.stop(),
      requestSoftStop: () => {
        if (bridge.onSoftStop) bridge.onSoftStop();
      },
      createInputStream: (prompt) => bridge.createInputStream(prompt),
      onModeTransition: bridge.onModeTransition,
    };
  }

  // ── Input stream for Claude SDK ─────────────────────────────────────

  private async *createInputStream(
    prompt: string | MultimodalBlock[],
  ): AsyncGenerator<HarnessUserMessage, void, unknown> {
    yield {
      type: "user" as const,
      session_id: "",
      message: { role: "user" as const, content: prompt },
      parent_tool_use_id: null,
    };
  }
}
